/**
 * Parking Request Sorting Strategies
 *
 * Different orderings for greedy placement algorithm.
 * Greedy processes requests sequentially - order matters!
 *
 * WHY: Placing constrained requests first prevents blocking.
 *
 * WHEN TO USE EACH:
 * - arrival: Realistic, respects actual timeline (default)
 * - size-constrained: Heterogeneous spots (some Small-only, some Flex)
 * - longest-dwell: Mix of quick-turns and overnights
 * - latest-departure: Overnight parking optimization
 * - hybrid: Complex scenarios with multiple constraints
 */

import { canAircraftUseSpot } from "./sizeCategories";
import { ParkingRequest, Spot } from "./types";

export type SortingStrategy =
  | "arrival"
  | "size-constrained"
  | "longest-dwell"
  | "latest-departure"
  | "hybrid"
  | "largest-first";

export type SortingFunction = (
  requests: ParkingRequest[],
  spots: Spot[]
) => ParkingRequest[];

/**
 * Sort by arrival time (current behavior)
 *
 * WHEN TO USE: Realistic simulation, respects timeline
 * LIMITATION: May waste flexible spots on flexible requests
 *
 * @param requests - Parking requests to sort
 * @param spots - Available spots (not used, but kept for consistent API)
 * @returns Sorted requests (earliest arrival first)
 */
export function sortByArrival(
  requests: ParkingRequest[],
  _spots: Spot[]
): ParkingRequest[] {
  return [...requests].sort((a, b) => {
    if (a.arrivalBucket !== b.arrivalBucket) {
      return a.arrivalBucket - b.arrivalBucket;
    }
    // Stable sort: maintain original order for ties
    return 0;
  });
}

/**
 * Sort by size constraint (fewest compatible spots first)
 *
 * WHEN TO USE: Heterogeneous spot policies
 * EXAMPLE: Large aircraft (2 compatible spots) before Small (5 spots)
 * WHY: Prevents wasting flexible spots on flexible requests
 *
 * @param requests - Parking requests to sort
 * @param spots - Available spots (used to count compatibility)
 * @returns Sorted requests (most constrained first)
 */
export function sortBySizeConstrained(
  requests: ParkingRequest[],
  spots: Spot[]
): ParkingRequest[] {
  return [...requests].sort((a, b) => {
    const aCompatible = countCompatibleSpots(a, spots);
    const bCompatible = countCompatibleSpots(b, spots);

    if (aCompatible !== bCompatible) {
      return aCompatible - bCompatible; // Fewer spots = higher priority
    }

    // Tiebreaker: arrival time
    return a.arrivalBucket - b.arrivalBucket;
  });
}

/**
 * Sort by dwell length (longest first)
 *
 * WHEN TO USE: Mix of short and long dwells
 * EXAMPLE: 12-hour overnight before 2-hour quick-turn
 * WHY: Long dwells harder to fit later, need priority
 *
 * @param requests - Parking requests to sort
 * @param spots - Available spots (not used, but kept for consistent API)
 * @returns Sorted requests (longest dwell first)
 */
export function sortByLongestDwell(
  requests: ParkingRequest[],
  _spots: Spot[]
): ParkingRequest[] {
  return [...requests].sort((a, b) => {
    const aDwell = getDwellLength(a);
    const bDwell = getDwellLength(b);

    if (aDwell !== bDwell) {
      return bDwell - aDwell; // Longer dwell = higher priority
    }

    // Tiebreaker: arrival time
    return a.arrivalBucket - b.arrivalBucket;
  });
}

/**
 * Sort by departure time (latest first)
 *
 * WHEN TO USE: Overnight parking optimization
 * EXAMPLE: Departs-tomorrow before departs-in-2-hours
 * WHY: Long-staying aircraft should get prime spots
 *
 * @param requests - Parking requests to sort
 * @param spots - Available spots (not used, but kept for consistent API)
 * @returns Sorted requests (latest departure first)
 */
export function sortByLatestDeparture(
  requests: ParkingRequest[],
  _spots: Spot[]
): ParkingRequest[] {
  return [...requests].sort((a, b) => {
    if (a.departureBucket !== b.departureBucket) {
      return b.departureBucket - a.departureBucket; // Later departure = higher priority
    }

    // Tiebreaker: arrival time
    return a.arrivalBucket - b.arrivalBucket;
  });
}

/**
 * Hybrid strategy: size-constrained with dwell length tiebreaker
 *
 * WHEN TO USE: Complex scenarios with multiple constraints
 * ALGORITHM:
 *   1. Count compatible spots for each request
 *   2. Sort by compatibility (fewer spots = higher priority)
 *   3. Tiebreaker: longest dwell
 *   4. Final tiebreaker: earliest arrival
 *
 * @param requests - Parking requests to sort
 * @param spots - Available spots (used to count compatibility)
 * @returns Sorted requests (most constrained + longest dwell first)
 */
export function sortByHybrid(
  requests: ParkingRequest[],
  spots: Spot[]
): ParkingRequest[] {
  return [...requests].sort((a, b) => {
    // Primary: size constraint
    const aCompatible = countCompatibleSpots(a, spots);
    const bCompatible = countCompatibleSpots(b, spots);

    if (aCompatible !== bCompatible) {
      return aCompatible - bCompatible; // Fewer spots = higher priority
    }

    // Secondary: dwell length
    const aDwell = getDwellLength(a);
    const bDwell = getDwellLength(b);

    if (aDwell !== bDwell) {
      return bDwell - aDwell; // Longer dwell = higher priority
    }

    // Tertiary: arrival time
    return a.arrivalBucket - b.arrivalBucket;
  });
}

/**
 * Sort by aircraft size (largest first)
 *
 * WHEN TO USE: Maximize spot utilization by placing large aircraft first
 * EXAMPLE: Large (6000/7500) before Very-Small (Cessna 172)
 * WHY: Large aircraft have fewer compatible spots and are harder to place
 *
 * @param requests - Parking requests to sort
 * @param spots - Available spots (not used, but kept for consistent API)
 * @returns Sorted requests (largest aircraft first)
 */
export function sortByLargestFirst(
  requests: ParkingRequest[],
  _spots: Spot[]
): ParkingRequest[] {
  return [...requests].sort((a, b) => {
    const aSizeRank = getSizeRank(a.sizeCategory);
    const bSizeRank = getSizeRank(b.sizeCategory);

    if (aSizeRank !== bSizeRank) {
      return bSizeRank - aSizeRank; // Higher rank (larger) = higher priority
    }

    // Tiebreaker: arrival time
    return a.arrivalBucket - b.arrivalBucket;
  });
}

/**
 * Get sorting function by strategy name
 *
 * @param strategy - Strategy name
 * @returns Sorting function
 * @throws Error if strategy is unknown
 */
export function getSortingStrategy(strategy: SortingStrategy): SortingFunction {
  switch (strategy) {
    case "arrival":
      return sortByArrival;
    case "size-constrained":
      return sortBySizeConstrained;
    case "longest-dwell":
      return sortByLongestDwell;
    case "latest-departure":
      return sortByLatestDeparture;
    case "hybrid":
      return sortByHybrid;
    case "largest-first":
      return sortByLargestFirst;
    default:
      throw new Error(`Unknown sorting strategy: ${strategy}`);
  }
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Count compatible spots for a parking request
 *
 * @param request - Parking request to check
 * @param spots - Available spots
 * @returns Number of spots that allow this request's aircraft size
 */
function countCompatibleSpots(request: ParkingRequest, spots: Spot[]): number {
  return spots.filter((spot) => canAircraftUseSpot(request, spot)).length;
}

/**
 * Calculate dwell length in buckets
 *
 * @param request - Parking request to measure
 * @returns Dwell length (departureBucket - arrivalBucket)
 */
function getDwellLength(request: ParkingRequest): number {
  return request.departureBucket - request.arrivalBucket;
}

/**
 * Get numeric rank for aircraft size category
 *
 * @param size - Size category
 * @returns Numeric rank (0-4, higher = larger aircraft)
 */
function getSizeRank(size: ParkingRequest["sizeCategory"]): number {
  const sizeRanking = {
    "very-small": 0,
    "small": 1,
    "midsize": 2,
    "super-midsize": 3,
    "large": 4,
  };
  return sizeRanking[size];
}
